import React, { useState, useCallback } from 'react'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Heading from '@react/react-spectrum/Heading'
import Link from '@react/react-spectrum/Link'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import Textfield from '@react/react-spectrum/Textfield'
import { warning } from '@react/react-spectrum/Toast'

import Edit from '@react/react-spectrum/Icon/Edit'
import Delete from '@react/react-spectrum/Icon/Delete'

import MediaUpload from 'components/MediaUpload'

import A from 'services/analytics'
import { collectOpprotunities } from 'services/opportunity'
import { deleteMetadata, updateMetadata, useProjectQuery } from 'services/project'
import { getProxiedPreviewLink, useProject } from 'hooks'

export default function MetadataSettings() {
  const { project, type, canEdit } = useProject()
  const projectId = project?.meta.id
  const { refetch: refetchProject } = useProjectQuery(projectId)

  const [metadata, setMetadata] = useState(project?.metadata || {})
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const disabled = !canEdit

  const previewLink = getProxiedPreviewLink({ ...project, alias: metadata?.alias || project?.alias })

  const editMetadata = useCallback(
    (data) => {
      // Security check: Verify user has edit permissions before updating metadata
      if (!canEdit) {
        warning('You are not allowed to make changes to this project. Ask project owner for View & Edit permission.')
        setIsLoading(false)
        setIsDialogOpen(false)
        return
      }

      const { drNumber, ...metadata } = data
      A.triggerUpdateAlias()
      setIsLoading(true)
      setIsDialogOpen(false)
      collectOpprotunities([{ drNumber, projectID: projectId }])
      updateMetadata(projectId, metadata, project)
        .then((newMetadata) => {
          setMetadata(newMetadata)
          refetchProject({ id: projectId })
        })
        .finally(() => setIsLoading(false))
    },
    [projectId, project, canEdit, refetchProject]
  )

  const removeMetadata = useCallback(() => {
    // Security check: Verify user has edit permissions before deleting metadata
    if (!canEdit) {
      warning('You are not allowed to make changes to this project. Ask project owner for View & Edit permission.')
      return
    }

    A.triggerDeleteAlias()
    deleteMetadata(projectId).then(() => {
      setMetadata({})
      refetchProject({ id: projectId })
    })
  }, [projectId, canEdit, refetchProject])

  let state = 'NOT_SET' // NOT_SET, VALID, EXPIRED

  // also read project alias for backward-compatibility
  const alias = metadata?.alias || project?.alias

  // if metadata is missing, assume pre-refactoring alias and set refactoring date
  const expirationDate = new Date(metadata?.expiresAt || '2024-08-23T12:35:00.702Z').toLocaleDateString()

  if (alias) {
    if (metadata.expiresAt > Date.now()) {
      state = 'VALID'
    } else {
      // expired or old, not migrated metadata
      state = 'EXPIRED'
    }
  }

  if (!['web-modular', 'customer-hub', 'web-social', 'customer-experience-v2'].includes(type)) {
    return null
  }

  const onClose = () => setIsDialogOpen(false)

  return (
    <section>
      <Heading size='4'>Alias & Metadata</Heading>
      <p>
        Use when the link's appearance is essential or if you need to extend its expiration <b>beyond 30 days</b>. It
        offers a user-friendly link, e.g. https://dsn.adobe.com/p/lumaxadobe, and a customized link card for
        Slack/Teams.
      </p>
      {state === 'NOT_SET' && (
        <>
          <p>
            <em>Alias is</em> <b>not set</b>
          </p>
          <ModalTrigger>
            <Button
              variant='action'
              icon={<Edit />}
              aria-label='create alias'
              label='Create alias'
              disabled={disabled || isLoading}
            />
            <MetadataSettingsDialog
              projectId={projectId}
              onSubmit={editMetadata}
              onCancel={onClose}
              open={isDialogOpen}
              {...metadata}
            />
          </ModalTrigger>
        </>
      )}
      {state === 'VALID' && (
        <>
          <Link href={previewLink}>{previewLink}</Link>
          <p>
            <em>Alias is</em> <b>valid through {expirationDate}</b>
          </p>
          <ModalTrigger>
            <Button
              variant='action'
              icon={<Edit />}
              aria-label='Update alias'
              label='Update alias'
              disabled={disabled || isLoading}
            />
            <MetadataSettingsDialog
              projectId={projectId}
              onSubmit={editMetadata}
              onCancel={onClose}
              open={isDialogOpen}
              {...metadata}
            />
          </ModalTrigger>
          <ModalTrigger>
            <Button
              variant='action'
              icon={<Delete />}
              aria-label='Delete alias'
              label='Delete alias'
              disabled={disabled || isLoading}
            />
            <DeleteMetadataDialog projectId={projectId} onConfirm={removeMetadata} />
          </ModalTrigger>
        </>
      )}
      {state === 'EXPIRED' && (
        <>
          <Link href={previewLink}>{previewLink}</Link>
          <p>
            <em>Alias </em> <b>expired on {expirationDate}</b>
          </p>
          <ModalTrigger>
            <Button
              variant='action'
              icon={<Edit />}
              aria-label='Update alias'
              label='Update alias'
              disabled={disabled || isLoading}
            />
            <MetadataSettingsDialog
              projectId={projectId}
              onSubmit={editMetadata}
              onCancel={onClose}
              open={isDialogOpen}
              {...metadata}
            />
          </ModalTrigger>
          <ModalTrigger>
            <Button
              variant='action'
              icon={<Delete />}
              aria-label='Delete alias'
              label='Delete alias'
              disabled={disabled || isLoading}
            />
            <DeleteMetadataDialog projectId={projectId} onConfirm={removeMetadata} />
          </ModalTrigger>
        </>
      )}
    </section>
  )
}
MetadataSettings.defaultProps = {
  metadata: {},
}

function DeleteMetadataDialog({ projectId, ...otherProps }) {
  return (
    <Dialog
      title='Delete metadata?'
      variant='destructive'
      confirmLabel='Remove'
      cancelLabel='Cancel'
      role='dialog'
      {...otherProps}
    >
      <p>
        Removing metadata and alias will disable the previous alias link and may prevent the preview access to this
        project.
        <br />
        It will also allow others to use this alias in the future.
      </p>
    </Dialog>
  )
}

function MetadataSettingsDialog({
  projectId,
  alias: _alias = '',
  drNumber: _drNumber = '',
  title: _title = '',
  description: _description = '',
  image: _image = '',
  onSubmit,
  ...otherProps
}) {
  const [alias, setAlias] = useState(_alias)
  const [drNumber, setDrNumber] = useState(_drNumber)
  const [title, setTitle] = useState(_title)
  const [description, setDescription] = useState(_description)
  const [image, setImage] = useState(_image)

  const handleSubmit = () => onSubmit({ alias, title, description, image, drNumber })

  return (
    <Dialog
      title='Edit metadata'
      onConfirm={handleSubmit}
      confirmLabel='Submit'
      confirmDisabled={!alias}
      cancelLabel='Cancel'
      role='dialog'
      {...otherProps}
    >
      <FieldLabel label='Alias*' labelFor='alias'>
        <Textfield name='alias' value={alias} onChange={setAlias} />
      </FieldLabel>
      <FieldLabel label='Preview card title' labelFor='previewTitle'>
        <Textfield name='previewTitle' value={title} onChange={setTitle} />
      </FieldLabel>
      <FieldLabel label='Preview card description' labelFor='previewDescription'>
        <Textfield name='previewDescription' value={description} onChange={setDescription} />
      </FieldLabel>
      <FieldLabel label='Preview card image' labelFor='previewImage'>
        <MediaUpload
          type='image'
          value={image}
          onUpload={setImage}
          projectId={projectId}
          path='metadata-image'
          withTextfield={true}
        />
      </FieldLabel>
      <FieldLabel label='DR Number' labelFor='drNumber'>
        <Textfield name='drNumber' value={drNumber} onChange={setDrNumber} placeholder='DRXXXXXX' />
      </FieldLabel>
    </Dialog>
  )
}
